import { Fragment, ReactNode } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { buttonClassname } from '../Atoms/Buttons'

interface DropdownItem {
  label: string
  onClick?: () => void
  href?: string
  icon?: React.ComponentType<{ className?: string }>
  danger?: boolean
}

interface DropdownSection {
  items: DropdownItem[]
}

interface DropdownProps {
  label: string | ReactNode
  items?: DropdownSection[] // Array of sections, each having an array of items for dividers
  className?: string
  buttonClassName?: string
  menuClassName?: string
  icon?: React.ComponentType<{ className?: string }>
  align?: 'left' | 'right'
  hideChevron?: boolean
}

export default function Dropdown({
  label,
  items = [],
  className = '',
  buttonClassName = '',
  menuClassName = '',
  icon: Icon,
  align = 'right',
  hideChevron = false,
}: DropdownProps) {
  return (
    <Menu
      as="div"
      className={classNames('relative inline-block text-left', className)}
    >
      <div>
        <Menu.Button
          className={classNames(
            buttonClassname,
            'inline-flex w-full gap-x-1.5',
            buttonClassName
          )}
        >
          {Icon && (
            <Icon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
          )}
          {label}
          {!hideChevron && (
            <ChevronDownIcon className="-mr-1 h-5 w-5" aria-hidden="true" />
          )}
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={classNames(
            'absolute z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
            align === 'right' ? 'right-0' : 'left-0',
            menuClassName
          )}
        >
          {items.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className="py-1 border-b border-gray-100 last:border-0"
            >
              {section.items.map((item, itemIndex) => (
                <Menu.Item key={itemIndex}>
                  {({ active }) => (
                    <button
                      onClick={item.onClick}
                      className={classNames(
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        item.danger
                          ? 'text-red-700 hover:bg-red-50 hover:text-red-900'
                          : '',
                        'group flex w-full items-center px-4 py-2 text-sm'
                      )}
                    >
                      {item.icon && (
                        <item.icon
                          className={classNames(
                            'mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500',
                            item.danger
                              ? 'text-red-400 group-hover:text-red-500'
                              : ''
                          )}
                          aria-hidden="true"
                        />
                      )}
                      {item.label}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
